import { Component } from "@theme/component";
import { onAnimationEnd } from "@theme/utilities";

/** localStorage: when the shopper last closed the pop-up, or `subscribed` once they have signed up. */
const STORAGE_KEY = "newsletter-popup";
/** sessionStorage: set once the pop-up has been closed during this visit. */
const SESSION_KEY = "newsletter-popup-closed";
/** sessionStorage: set while the pop-up's form is being sent, so the reloaded page reopens it. */
const SUBMITTED_KEY = "newsletter-popup-submitted";
const DAY = 24 * 60 * 60 * 1000;

/**
 * The Newsletter pop-up section. Opens itself after a delay unless the shopper closed it within the
 * last few days or has subscribed. With the page dimmed it opens as a modal dialog, which also stops
 * the page scrolling; otherwise it sits over the page and leaves the rest of it usable.
 *
 * Shopify's customer form reloads the page when it is sent, so the pop-up reopens straight away on
 * the page that comes back, showing the form's confirmation or error.
 *
 * A link to the pop-up's hash (`#newsletter` by default) opens it at any time, whatever the delay,
 * device settings or a previous close say. A link to another page with that hash opens it there.
 *
 * In the theme editor it opens only while the section or one of its blocks is selected, or from a
 * link.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
class NewsletterPopupComponent extends Component {
  requiredRefs = ["dialog"];

  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this.refs;
    dialog.addEventListener("submit", this.#handleSubmit);
    dialog.addEventListener("cancel", this.#handleCancel);
    dialog.addEventListener("keydown", this.#handleKeyDown);

    if (this.linkHash) {
      document.addEventListener("click", this.#handleLinkClick);
      window.addEventListener("hashchange", this.#handleHashChange);
    }

    if (window.Shopify?.designMode) {
      document.addEventListener("shopify:section:select", this.#handleSelect);
      document.addEventListener("shopify:block:select", this.#handleSelect);
      document.addEventListener(
        "shopify:section:deselect",
        this.#handleDeselect,
      );

      // Changing a setting re-renders the section, replacing this element while it is selected
      if (
        editorSelection &&
        this.closest(".shopify-section")?.id === editorSelection
      ) {
        this.open();
      }
      return;
    }

    if (
      new URL(window.location.href).searchParams.get("customer_posted") ===
      "true"
    ) {
      storage("local")?.setItem(STORAGE_KEY, "subscribed");
    }

    if (storage("session")?.getItem(SUBMITTED_KEY)) {
      storage("session")?.removeItem(SUBMITTED_KEY);
      this.open();
      return;
    }

    if (this.#isLinkHash(window.location.hash)) {
      this.open();
      return;
    }

    if (!this.#shouldOpen) return;

    this.#timer = window.setTimeout(() => this.open(), this.delay * 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.clearTimeout(this.#timer);
    document.documentElement.removeAttribute("scroll-lock");
    document.removeEventListener("click", this.#handleLinkClick);
    window.removeEventListener("hashchange", this.#handleHashChange);
    document.removeEventListener("shopify:section:select", this.#handleSelect);
    document.removeEventListener("shopify:block:select", this.#handleSelect);
    document.removeEventListener(
      "shopify:section:deselect",
      this.#handleDeselect,
    );
  }

  /**
   * Opens the pop-up.
   */
  open() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    window.clearTimeout(this.#timer);
    dialog.classList.remove("is-closing");

    if (this.hasAttribute("data-modal")) {
      dialog.showModal();
      document.documentElement.setAttribute("scroll-lock", "");
    } else {
      // Not show(): that moves focus into the pop-up, away from whatever the shopper is doing
      dialog.setAttribute("open", "");
    }
  }

  /**
   * Closes the pop-up and, outside the theme editor, remembers that the shopper closed it.
   */
  close = async () => {
    const { dialog } = this.refs;

    if (!dialog.open || dialog.classList.contains("is-closing")) return;

    if (!window.Shopify?.designMode) this.#remember();

    dialog.classList.add("is-closing");
    await onAnimationEnd(dialog, undefined, { subtree: false });

    dialog.close();
    dialog.classList.remove("is-closing");
    document.documentElement.removeAttribute("scroll-lock");
  };

  /** @returns {number} Seconds to wait before opening. */
  get delay() {
    return Number(this.dataset.delay) || 0;
  }

  /** @returns {number} Days to wait after the shopper closes the pop-up. */
  get dismissDays() {
    return Number(this.dataset.dismissDays) || 0;
  }

  /** @returns {string} The hash, without `#`, that links use to open the pop-up. Empty when off. */
  get linkHash() {
    return this.dataset.linkHash ?? "";
  }

  /**
   * Whether a URL hash is this pop-up's. Browsers percent-encode a hash, so it is decoded first.
   * @param {string} hash
   */
  #isLinkHash(hash) {
    if (!this.linkHash || !hash) return false;

    try {
      return decodeURIComponent(hash.slice(1)) === this.linkHash;
    } catch {
      return false;
    }
  }

  get #shouldOpen() {
    if (this.hasAttribute("data-no-auto-open")) return false;

    const isDesktop = window.matchMedia("(min-width: 750px)").matches;
    if (
      !this.hasAttribute(isDesktop ? "data-show-desktop" : "data-show-mobile")
    )
      return false;

    if (storage("session")?.getItem(SESSION_KEY)) return false;

    const stored = storage("local")?.getItem(STORAGE_KEY);
    if (stored === "subscribed") return false;

    const closedAt = Number(stored);
    return !closedAt || Date.now() - closedAt >= this.dismissDays * DAY;
  }

  #remember() {
    storage("session")?.setItem(SESSION_KEY, "true");

    const local = storage("local");
    if (local && local.getItem(STORAGE_KEY) !== "subscribed") {
      local.setItem(STORAGE_KEY, String(Date.now()));
    }

    // After a send, closing clears ?customer_posted so a refresh doesn't show the message again
    const url = new URL(window.location.href);
    const openedByHash = this.#isLinkHash(url.hash);
    if (url.searchParams.has("customer_posted") || openedByHash) {
      url.searchParams.delete("customer_posted");
      url.hash = "";
      history.replaceState(history.state, "", url);
    }
  }

  /**
   * A link to this page with the pop-up's hash opens the pop-up instead of jumping. Links to other
   * pages go there, and the pop-up opens when that page loads.
   * @param {MouseEvent} event
   */
  #handleLinkClick = (event) => {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest("a[href]");
    if (!(link instanceof HTMLAnchorElement) || !this.#isLinkHash(link.hash))
      return;
    if (
      link.origin !== window.location.origin ||
      link.pathname !== window.location.pathname
    )
      return;

    event.preventDefault();

    // A link in the menu drawer would otherwise leave the drawer open over the pop-up
    const drawer = /** @type {{ close?: () => void } | null} */ (
      /** @type {unknown} */ (link.closest("header-drawer"))
    );
    drawer?.close?.();

    this.open();
  };

  /** A hash set some other way, such as by a script or typed into the address bar. */
  #handleHashChange = () => {
    if (this.#isLinkHash(window.location.hash)) this.open();
  };

  #handleSubmit = () => {
    storage("session")?.setItem(SUBMITTED_KEY, "true");
  };

  /**
   * Escape on a modal pop-up: animate out rather than letting the browser close it at once.
   * @param {Event} event
   */
  #handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /**
   * Escape on a pop-up that isn't modal, which the browser leaves alone.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== "Escape" || this.refs.dialog.matches(":modal")) return;

    event.preventDefault();
    this.close();
  };

  /**
   * The editor fires select on the section's wrapper, or on the selected block.
   * @param {Event} event
   */
  #handleSelect = (event) => {
    if (this.#isInside(event.target)) this.open();
  };

  /** @param {Event} event */
  #handleDeselect = (event) => {
    if (this.#isInside(event.target)) this.close();
  };

  /**
   * Whether this pop-up is, or is inside, the element, or the element is one of its blocks.
   * @param {EventTarget | null} element
   */
  #isInside(element) {
    if (!(element instanceof Element)) return false;
    return element.contains(this) || this.contains(element);
  }
}

/**
 * The id of the section wrapper selected in the theme editor. It is kept here, outside any one
 * pop-up, because a re-render replaces the pop-up but leaves its section selected without firing
 * select again.
 * @type {string | null}
 */
let editorSelection = null;

if (window.Shopify?.designMode) {
  /** @param {Event} event */
  const select = (event) => {
    editorSelection =
      event.target instanceof Element
        ? (event.target.closest(".shopify-section")?.id ?? null)
        : null;
  };
  document.addEventListener("shopify:section:select", select);
  document.addEventListener("shopify:block:select", select);
  document.addEventListener("shopify:section:deselect", () => {
    editorSelection = null;
  });
}

/**
 * Storage can be missing or throw (private windows, blocked site data), so callers skip it then.
 * @param {'local' | 'session'} type
 * @returns {Storage | null}
 */
function storage(type) {
  try {
    const store =
      type === "local" ? window.localStorage : window.sessionStorage;
    store.getItem(STORAGE_KEY);
    return store;
  } catch {
    return null;
  }
}

if (!customElements.get("newsletter-popup-component")) {
  customElements.define("newsletter-popup-component", NewsletterPopupComponent);
}
